import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { toast } from "sonner";
import { Plus, X, History } from "lucide-react";

const COLUMNS = [
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In Progress" },
  { id: "done", label: "Done" },
  { id: "blocked", label: "Blocked" },
];

const EMPTY_FORM = {
  title: "", description: "", priority: "medium", due_date: "",
  assignee_user_id: "", linked_campaign_id: "", linked_client_id: "",
  linked_account_id: "", linked_landing_page_id: "",
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [lps, setLps] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status");

  const load = () => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get("/tasks", { params }).then((r) => setTasks(r.data));
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/clients/accounts/all").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/landing-pages").then((r) => setLps(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    const payload = { ...form };
    ["assignee_user_id", "linked_campaign_id", "linked_client_id", "linked_account_id", "linked_landing_page_id", "due_date"]
      .forEach(k => { if (!payload[k]) delete payload[k]; });
    await api.post("/tasks", payload);
    toast.success("Task created");
    setForm(null);
    load();
  };

  const patch = async (id, body) => {
    await api.patch(`/tasks/${id}`, body);
    load();
    if (detail && detail.id === id) openDetail(id);
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/tasks/${id}`);
    setDetail(data);
  };

  const priorityTone = (p) => p === "urgent" ? "critical" : p === "high" ? "warning" : p === "medium" ? "primary" : "muted";

  return (
    <Layout
      title="Tasks & Daily Operations"
      subtitle={`${tasks.length} tasks${statusFilter ? ` · filter: ${statusFilter}` : ""}`}
      actions={
        <button onClick={() => setForm({ ...EMPTY_FORM })} data-testid="new-task-btn" className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-sm hover:bg-primary/90">
          <Plus className="w-3.5 h-3.5" /> New Task
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tasks-kanban">
        {COLUMNS.map((col) => {
          const items = tasks.filter((t) => t.status === col.id);
          return (
            <div key={col.id} className="bg-card border border-border rounded-sm">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="label-mono">{col.label}</div>
                <span className="text-xs font-semibold tabular">{items.length}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[160px]">
                {items.map((t) => (
                  <div key={t.id} onClick={() => openDetail(t.id)} className="bg-background border border-border rounded-sm p-3 cursor-pointer hover:border-primary/50" data-testid={`task-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-medium text-sm leading-snug">{t.title}</div>
                      <StatusBadge tone={priorityTone(t.priority)}>{t.priority}</StatusBadge>
                    </div>
                    {t.description && <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{t.description}</div>}
                    {(t.linked_campaign_name || t.linked_client_name || t.linked_landing_page_title) && (
                      <div className="text-[11px] text-primary mb-1">
                        → {t.linked_campaign_name || t.linked_client_name || t.linked_account_name || t.linked_landing_page_title}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{t.due_date ? `Due ${formatDate(t.due_date)}` : "No due date"}</span>
                      <span>{t.assignee_name || "Unassigned"}</span>
                    </div>
                    <div className="mt-2">
                      <select
                        value={t.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => patch(t.id, { status: e.target.value })}
                        data-testid={`task-status-${t.id}`}
                        className="w-full bg-transparent border border-border text-[11px] rounded-sm px-1 py-0.5"
                      >
                        {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No tasks</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      {form && (
        <TaskForm
          form={form} setForm={setForm}
          users={users} clients={clients} campaigns={campaigns} accounts={accounts} lps={lps}
          onSave={create} onClose={() => setForm(null)} title="New Task"
        />
      )}

      {/* Detail drawer */}
      {detail && (
        <TaskDetail
          task={detail}
          users={users} clients={clients} campaigns={campaigns} accounts={accounts} lps={lps}
          onClose={() => setDetail(null)}
          onPatch={(body) => patch(detail.id, body)}
        />
      )}
    </Layout>
  );
}

function TaskForm({ form, setForm, users, clients, campaigns, accounts, lps, onSave, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="task-form-modal">
        <div className="flex justify-between items-start mb-4">
          <div className="font-heading text-lg font-medium">{title}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm" data-testid="task-title-input" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full h-20 px-3 py-2 border border-border rounded-sm text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Labeled label="Priority">
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </Labeled>
            <Labeled label="Due date">
              <input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm" />
            </Labeled>
          </div>
          <Labeled label="Assignee">
            <select value={form.assignee_user_id || ""} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm" data-testid="task-assignee">
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </Labeled>
          <div className="grid grid-cols-2 gap-2">
            <Labeled label="Linked Client">
              <select value={form.linked_client_id || ""} onChange={(e) => setForm({ ...form, linked_client_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="">—</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Labeled>
            <Labeled label="Linked Account">
              <select value={form.linked_account_id || ""} onChange={(e) => setForm({ ...form, linked_account_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Labeled>
            <Labeled label="Linked Campaign">
              <select value={form.linked_campaign_id || ""} onChange={(e) => setForm({ ...form, linked_campaign_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="">—</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Labeled>
            <Labeled label="Linked Landing Page">
              <select value={form.linked_landing_page_id || ""} onChange={(e) => setForm({ ...form, linked_landing_page_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="">—</option>
                {lps.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
              </select>
            </Labeled>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onSave} data-testid="task-create-btn" className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm">Save</button>
          <button onClick={onClose} className="flex-1 h-10 border border-border text-sm rounded-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TaskDetail({ task, users, clients, campaigns, accounts, lps, onClose, onPatch }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="task-detail-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-heading text-lg font-medium">{task.title}</div>
            <div className="text-xs text-muted-foreground mt-1">Created by {task.created_by_name || "—"} · {formatDate(task.created_at)}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {task.description && <div className="text-sm bg-secondary/20 border border-border rounded-sm p-3 mb-4">{task.description}</div>}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Labeled label="Status">
            <select value={task.status} onChange={(e) => onPatch({ status: e.target.value })} className="w-full h-9 px-2 border border-border rounded-sm text-sm" data-testid="task-detail-status">
              {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Labeled>
          <Labeled label="Priority">
            <select value={task.priority} onChange={(e) => onPatch({ priority: e.target.value })} className="w-full h-9 px-2 border border-border rounded-sm text-sm">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
          </Labeled>
          <Labeled label="Assignee">
            <select value={task.assignee_user_id || ""} onChange={(e) => onPatch({ assignee_user_id: e.target.value || null })} className="w-full h-9 px-2 border border-border rounded-sm text-sm">
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Labeled>
          <Labeled label="Due date">
            <input type="date" value={task.due_date || ""} onChange={(e) => onPatch({ due_date: e.target.value })} className="w-full h-9 px-2 border border-border rounded-sm text-sm" />
          </Labeled>
        </div>

        <div className="label-mono mb-2">Linked entities</div>
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          {task.linked_client_name && <a href={`/clients/${task.linked_client_id}`} className="px-2 py-1 border border-border rounded-sm hover:bg-secondary">Client: {task.linked_client_name}</a>}
          {task.linked_account_name && <a href={`/accounts/${task.linked_account_id}`} className="px-2 py-1 border border-border rounded-sm hover:bg-secondary">Account: {task.linked_account_name}</a>}
          {task.linked_campaign_name && <a href={`/campaigns/${task.linked_campaign_id}`} className="px-2 py-1 border border-border rounded-sm hover:bg-secondary">Campaign: {task.linked_campaign_name}</a>}
          {task.linked_landing_page_title && <a href={`/landing-pages/${task.linked_landing_page_id}`} className="px-2 py-1 border border-border rounded-sm hover:bg-secondary">Landing: {task.linked_landing_page_title}</a>}
          {!(task.linked_client_name || task.linked_account_name || task.linked_campaign_name || task.linked_landing_page_title) && <span className="text-muted-foreground">None</span>}
        </div>

        <div className="flex items-center gap-2 mt-4 mb-2"><History className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Activity History</div></div>
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {(task.history || []).map((h) => (
            <div key={h.id} className="text-xs py-1.5 border-b border-border last:border-b-0 flex justify-between">
              <span><b>{h.action}</b> {h.before && <>· {h.before} → {h.after}</>} {h.note && <span className="text-muted-foreground">· {h.note}</span>}</span>
              <span className="text-muted-foreground">{h.user_name} · {formatDate(h.at)}</span>
            </div>
          ))}
          {(!task.history || task.history.length === 0) && <div className="text-xs text-muted-foreground">No activity yet.</div>}
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      {children}
    </div>
  );
}
